import { useState, useEffect } from 'react';
import { useLang } from '../context/LanguageContext';
import en from '../lang/en';
import de from '../lang/de';
import it from '../lang/it';
import fr from '../lang/fr';
import { logo } from '../assets/media';

const SECTIONS = ['home', 'about', 'solutions', 'industries', 'contact'];

function Navbar() {
  const { lang, setLang } = useLang();
  const t = lang === 'de' ? de.nav : lang === 'it' ? it.nav : lang === 'fr' ? fr.nav : en.nav;
  const [menuOpen, setMenuOpen] = useState(false);
  const [active, setActive] = useState('home');

  const doScroll = (id) => {
    const container = document.querySelector('.scroll-container');
    const el = document.getElementById(id);
    if (container && el) {
      container.scrollTo({ top: el.offsetTop, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const container = document.querySelector('.scroll-container');
    if (!container) return;

    // Active section tracker
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible.length > 0) setActive(visible[0].target.id);
      },
      { root: container, threshold: [0.1, 0.3, 0.5, 0.7, 1.0] }
    );
    SECTIONS.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    // Browser back/forward — scroll to section from hash
    const onHashChange = () => {
      const id = window.location.hash.replace('#', '');
      if (SECTIONS.includes(id)) doScroll(id);
    };
    window.addEventListener('hashchange', onHashChange);

    // Scroll to section on initial load if hash present
    if (window.location.hash) onHashChange();

    return () => {
      observer.disconnect();
      window.removeEventListener('hashchange', onHashChange);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const scrollTo = (id) => {
    doScroll(id);
    // Push to browser history so back button works
    if (window.location.hash !== '#' + id) {
      window.history.pushState(null, '', '#' + id);
    }
    setMenuOpen(false);
  };

  const navItems = [
    { id: 'home',       label: t.home },
    { id: 'about',      label: t.about },
    { id: 'solutions',  label: t.solutions },
    { id: 'industries', label: t.industries },
    { id: 'contact',    label: t.contact },
  ];

  return (
    <nav className="navbar">
      <div className="logo">
        <span onClick={() => scrollTo('home')} style={{ cursor: 'pointer' }}>
          <img src={logo} alt="7 Elemente IT Solutions" className="logo-img" />
        </span>
      </div>

      <ul className="nav-links">
        {navItems.map(({ id, label }) => (
          <li key={id}>
            <a
              href={`#${id}`}
              className={active === id ? 'active' : ''}
              onClick={(e) => { e.preventDefault(); scrollTo(id); }}
            >
              {label}
            </a>
          </li>
        ))}
      </ul>

      <div className="nav-flags">
        <span
          className={`fi fi-gb flag${lang === 'en' ? ' flag-active' : ''}`}
          title="English"
          onClick={() => setLang('en')}
        />
        <span
          className={`fi fi-de flag${lang === 'de' ? ' flag-active' : ''}`}
          title="Deutsch"
          onClick={() => setLang('de')}
        />
        <span
          className={`fi fi-it flag${lang === 'it' ? ' flag-active' : ''}`}
          title="Italiano"
          onClick={() => setLang('it')}
        />
        <span
          className={`fi fi-fr flag${lang === 'fr' ? ' flag-active' : ''}`}
          title="Français"
          onClick={() => setLang('fr')}
        />
      </div>

      <button className="hamburger" onClick={() => setMenuOpen(o => !o)} aria-label="Menu">
        <span></span>
        <span></span>
        <span></span>
      </button>

      <div className={`mobile-menu${menuOpen ? ' open' : ''}`}>
        {navItems.map(({ id, label }) => (
          <a
            key={id}
            href={`#${id}`}
            className={active === id ? 'active' : ''}
            onClick={(e) => { e.preventDefault(); scrollTo(id); setMenuOpen(false); }}
          >
            {label}
          </a>
        ))}
      </div>
    </nav>
  );
}

export default Navbar;
